"use client"

import type React from "react"
import { useEffect, useState, useRef, useMemo, useCallback } from "react"
import { IonContent, IonPage } from "@ionic/react"
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Container,
  InputAdornment,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  TablePagination,
  Checkbox,
  TableFooter,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Zoom,
  Alert,
  Tooltip,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  TrendingUp,
  TrendingDown,
  Inventory2,
  AttachMoney,
  FilterList,
  Clear,
  KeyboardArrowUp,
  Error as ErrorIcon,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import axios from "axios"
import { requestID, baseURL, config } from "../../../common/utils"
import { getTlocations_production_centre } from "../../../api/stock-transfer/stock-transfer"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    maxHeight: "80vh",
  },
}))

const DetailDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    maxWidth: "90vw",
    maxHeight: "85vh",
  },
}))

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: 8,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
}))

// Optimized header container with transform for better performance
const FixedHeaderContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hidden'
})<{ hidden?: boolean }>(({ theme, hidden }) => ({
  position: "sticky",
  top: 0,
  zIndex: 10,
  backgroundColor: "white",
  borderBottom: "1px solid #e2e8f0",
  transform: hidden ? 'translateY(-100%)' : 'translateY(0)',
  transition: 'transform 0.2s ease-in-out',
  willChange: 'transform', // Hint browser for better performance
}))

const StickyTableHeader = styled(TableHead)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 5,
  backgroundColor: "#f5f7fa",
}))

interface InventoryItem {
  id: string
  dateCreated: string
  product_id: string
  productName: string
  description: string
  productCategory: string
  unit_price: string
  selling_price: string
  total_quantity_supplied: string
  total_quantity_consumed: string
  total_quantity_left: string
  bar_quantity_left: string
  bar_qty?: string
  user_id?: string
  name?: string;
  qty?: string;
  price?: string;
  category?: string;
  CurrentStock?: number;
  CurrentStockValue?: number;
  OpeningBalance?: number;
  OpeningValueBalance?: number;
  ClosingBalance?: number;
  ClosingValueBalance?: number;
  TotalPurchasedBetween?: number;
  TotalSoldBetween?: number;
  TotalAmountSoldBetween?: number;
  TotalPurchasedFromStartToNow?: number;
  TotalSoldFromStartToNow?: number;
  TotalAmountSoldFromStartToNow?: number;
  branch_id?: string;
  branch_measure?: string;
  business_id?: string;
  category_id?: string;
  grade_letter?: string;
  img_src?: string | null;
  instocks?: number;
  last_updated?: string;
  minSalePrice?: string;
  purchasePrice?: string;
  reorder_qty?: string;
  stockControlled?: string;
  totalQtyPosted?: number;
  totalQtySold?: number;
  type?: string;
  warehouse_measure?: string;
  TotalOutFlowBetween?: number;
}

interface DetailItem {
  product_id: string;
  product_name: string;
  category: string;
  quantity: number;
  value: number;
  price: number;
  date?: string;
  [key: string]: any;
}

const Inventory_business: React.FC = () => {
  // State declarations
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedInventory, setSelectedInventory] = useState<InventoryItem | null>(null)
  const [inventory, setInventory] = useState<InventoryItem[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(500)
  const [selected, setSelected] = useState<readonly string[]>([])
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  const [branches, setBranches] = useState<any[]>([])
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null)
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [dateFilterApplied, setDateFilterApplied] = useState(false)
  const [headerVisible, setHeaderVisible] = useState(true)
  const [showScrollTop, setShowScrollTop] = useState(false)
  
  // Detail dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [selectedMetric, setSelectedMetric] = useState('')
  const [detailData, setDetailData] = useState<DetailItem[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [metricTitle, setMetricTitle] = useState('')

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout>()
  const lastScrollYRef = useRef(0)

  // Memoized helper functions
  const parseNumber = useCallback((val: any): number => {
    if (val === null || val === undefined || val === '') return 0
    const num = parseFloat(String(val).replace(/[^0-9.]/g, ""))
    return isNaN(num) ? 0 : num
  }, [])

  const formatCurrency = useCallback((amount: string | number): string => {
    const numAmount = typeof amount === "string" ? parseNumber(amount) : amount
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(numAmount)
  }, [parseNumber])

  const formatDate = useCallback((dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }, [])

  const getStockStatus = useCallback((qtyLeft: number) => {
    if (qtyLeft === 0) return { label: "Out of Stock", color: "error" }
    if (qtyLeft < 10) return { label: "Low Stock", color: "warning" }
    return { label: "In Stock", color: "success" }
  }, [])

  // Optimized scroll handler with throttling
  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current) return

    const scrollTop = tableContainerRef.current.scrollTop
    
    // Throttle state updates
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = setTimeout(() => {
      // Only update if scroll direction changed significantly
      const scrollDelta = Math.abs(scrollTop - lastScrollYRef.current)
      
      if (scrollDelta > 50) { // Only update after significant scroll
        setHeaderVisible(scrollTop < 100)
        setShowScrollTop(scrollTop > 300)
        lastScrollYRef.current = scrollTop
      }
    }, 50) // 50ms throttle
  }, [])

  const scrollToTop = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  // Memoized data computations
  const filteredInventory = useMemo(() => {
    return inventory.filter((item) =>
      `${item.name || item.productName} ${item.description} ${item.dateCreated} ${item.category || item.productCategory} ${item.product_id}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    )
  }, [inventory, searchTerm])

  const paginatedInventory = useMemo(() => {
    return filteredInventory.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [filteredInventory, page, rowsPerPage])

  // Memoized statistics
  const statistics = useMemo(() => {
    const totalValue = inventory.reduce((total, item) => {
      const qty = item.ClosingBalance || item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0')
      const price = parseNumber(item.price || item.selling_price || item.minSalePrice || '0')
      return total + (qty * price)
    }, 0)

    const totalItems = inventory.reduce((total, item) => {
      return total + (item.ClosingBalance || item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0'))
    }, 0)

    const lowStockCount = inventory.filter((item) => {
      const qty = item.ClosingBalance || item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0')
      return qty > 0 && qty < 10
    }).length

    const outOfStockCount = inventory.filter((item) => {
      const qty = item.ClosingBalance || item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0')
      return qty === 0
    }).length

    return { totalValue, totalItems, lowStockCount, outOfStockCount }
  }, [inventory, parseNumber])

  // Memoized footer totals
  const filteredTotals = useMemo(() => {
    let closingValueTotal = 0
    let purchasedValueTotal = 0
    let soldValueTotal = 0
    let totalOutFlowTotal = 0
    let totalOutFlowTotalSum = 0

    let totalOpeningBalance = 0
    let totalOpeningValue = 0
    let totalPurchasedQty = 0
    let totalPurchasedValue = 0
    let totalSoldQty = 0
    let totalSoldValue = 0
    let totalClosingBalance = 0
    let totalClosingValue = 0
    let totalCurrentStock = 0
    let totalCurrentStockValue = 0
    let totalCalculatedValue = 0

    filteredInventory.forEach((item) => {
      const openingBalance = item.OpeningBalance || 0
      const openingValue = parseNumber(item.OpeningValueBalance || '0')
      const purchasedQty = parseNumber(item.TotalPurchasedBetween || '0')
      const soldQty = parseNumber(item.TotalSoldBetween || '0')
      const soldValue = parseNumber(item.TotalAmountSoldBetween || '0')
      const closingBalance = item.ClosingBalance || 0
      const closingValue = parseNumber(item.ClosingValueBalance || '0')
      const currentStock = item.CurrentStock || 0
      const currentStockValue = parseNumber(item.CurrentStockValue || '0')
      const itemPrice = parseNumber(item.price || item.selling_price || item.minSalePrice || '0')
      const calculatedValue = currentStock * itemPrice

      closingValueTotal += closingValue
      totalOutFlowTotal += Number(item.TotalOutFlowBetween || '0')
      const TotalOutFlowBetweenAmount = Number(item.TotalOutFlowBetween * itemPrice || '0')
      totalOutFlowTotalSum += TotalOutFlowBetweenAmount
      purchasedValueTotal += purchasedQty * itemPrice
      soldValueTotal += soldValue

      totalOpeningBalance += openingBalance
      totalOpeningValue += openingValue
      totalPurchasedQty += purchasedQty
      totalPurchasedValue += purchasedQty * itemPrice
      totalSoldQty += soldQty
      totalSoldValue += soldValue
      totalClosingBalance += closingBalance
      totalClosingValue += closingValue
      totalCurrentStock += currentStock
      totalCurrentStockValue += currentStockValue
      totalCalculatedValue += calculatedValue
    })

    return { 
      totalOpeningBalance, 
      totalOpeningValue, 
      totalPurchasedQty, 
      totalPurchasedValue,
      totalSoldQty, 
      totalSoldValue,
      totalClosingBalance,
      totalClosingValue,
      totalCurrentStock,
      totalCurrentStockValue,
      totalCalculatedValue,
      closingValueTotal,
      purchasedValueTotal,
      soldValueTotal,
      totalOutFlowTotal,
      totalOutFlowTotalSum
    }
  }, [filteredInventory, parseNumber])

  // Handler for viewing total details
  const handleViewDetails = useCallback(async (metricType: string, metricTitle: string) => {
    if (!selectedBranch || !startDate || !endDate) {
      setDetailError("Please select a branch and date range first")
      return
    }
    
    setSelectedMetric(metricType)
    setMetricTitle(metricTitle)
    setDetailLoading(true)
    setDetailError(null)
    setDetailDialogOpen(true)
    
    try {
      // Map metric types to API endpoints
      const endpointMap: Record<string, string> = {
        'opening-balance': 'warehouses/getOpeningBalanceDetails',
        'opening-value': 'warehouses/getOpeningValueDetails',
        'outflow-qty': 'warehouses/getOutflowQuantityDetails',
        'outflow-value': 'warehouses/getOutflowValueDetails',
        'purchased-qty': 'warehouses/getPurchasedQuantityDetails',
        'purchased-value': 'warehouses/getPurchasedValueDetails',
        'sold-qty': 'warehouses/getSoldQuantityDetails',
        'sold-value': 'warehouses/getSoldValueDetails',
        'closing-balance': 'warehouses/getClosingBalanceDetails',
        'closing-value': 'warehouses/getClosingValueDetails',
        'current-stock': 'warehouses/getCurrentStockDetails',
        'current-stock-value': 'warehouses/getCurrentStockValueDetails',
      }
      
      const endpoint = endpointMap[metricType]
      
      if (!endpoint) {
        throw new Error(`No endpoint configured for metric: ${metricType}`)
      }
      
      const payload = { 
        requestID: requestID(),
        branch_id: selectedBranch,
        startDate: startDate,
        endDate: endDate,
        // Optionally include product IDs if you want to filter by current selection
        product_ids: filteredInventory.map(item => item.product_id)
      }
      
      const res = await axios.post(`${baseURL}${endpoint}`, payload, config)
      
      console.log( res.data.data);
      if (res.data.status) {
        setDetailData(res.data.data || [])
      } else {
        throw new Error(res.data.message || 'Failed to fetch details')
      }
      
    } catch (err: any) {
      setDetailError(err.message || 'An error occurred while fetching details')
      console.error('Error fetching detail data:', err)
      setDetailData([])
    } finally {
      setDetailLoading(false)
    }
  }, [selectedBranch, startDate, endDate, filteredInventory])

  const handleCloseDetailDialog = useCallback(() => {
    setDetailDialogOpen(false)
    setDetailData([])
    setDetailError(null)
    setDetailLoading(false)
  }, [])

  // API calls
  const fetchBranches = useCallback(async () => {
    setBranchesLoading(true)
    try {
      const response = await getTlocations_production_centre()
      setBranches(response.data.data || [])
    } catch (error) {
      console.error("Failed to fetch branches", error)
    } finally {
      setBranchesLoading(false)
    }
  }, [])

  const fetchInventory = useCallback(async (branchId: string, startDateParam = "", endDateParam = "") => {
    if (!branchId) return
    
    try {
      setLoading(true)
      const payload = { 
        requestID: requestID(),
        branch_id: branchId,
        startDate: startDateParam,
        endDate: endDateParam
      }
      const res = await axios.post(`${baseURL}warehouses/getProductsBy_branch_id`, payload, config)
      setInventory(res.data.data || [])
      if (startDateParam && endDateParam) {
        setDateFilterApplied(true)
      }
    } catch (error) {
      console.error("Error fetching products:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Event handlers
  const handleDateFilter = useCallback(() => {
    if (!selectedBranch || !startDate || !endDate) return
    setPage(0)
    setDateFilterApplied(true)
    fetchInventory(selectedBranch, startDate, endDate)
  }, [selectedBranch, startDate, endDate, fetchInventory])

  const clearDateFilter = useCallback(() => {
    setStartDate("")
    setEndDate("")
    setDateFilterApplied(false)
    setPage(0)
    setInventory([])
  }, [])

  const handleBranchChange = useCallback((branchId: string) => {
    setSelectedBranch(branchId)
    setInventory([])
    setSearchTerm("")
    setPage(0)
    setSelected([])
    setStartDate("")
    setEndDate("")
    setDateFilterApplied(false)
  }, [])

  const handleOpen = useCallback((item: InventoryItem) => {
    setSelectedInventory(item)
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    setSelectedInventory(null)
  }, [])

  const handleSelectAllClick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = paginatedInventory.map((n) => n.product_id)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }, [paginatedInventory])

  const handleClick = useCallback((event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id)
    let newSelected: readonly string[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      )
    }
    setSelected(newSelected)
  }, [selected])

  const isSelected = useCallback((id: string) => selected.indexOf(id) !== -1, [selected])

  const handleSubmitSelected = useCallback(async () => {
    if (!selectedBranch) return
    
    try {
      setLoading(true)
      const payload = { 
        requestID: requestID(),
        data: selected,
        branch_id: selectedBranch,
      }
      const res = await axios.post(`${baseURL}warehouses/deleteInventory`, payload, config)
      fetchInventory(selectedBranch, startDate, endDate)
    } catch (error) {
      console.error("Error fetchInventory :", error)
    } finally {
      setLoading(false)
    }
  
    setSelected([])
  }, [selectedBranch, selected, startDate, endDate, fetchInventory])

  // Derived values
  const areBothDatesSelected = useMemo(() => startDate && endDate, [startDate, endDate])

  // Calculate detail totals for the dialog
  // const detailTotals = useMemo(() => {
  //   const totalQuantity = detailData.reduce((sum, item) => sum + (item.quantity || 0), 0)
  //   const totalValue = detailData.reduce((sum, item) => sum + (item.value || 0), 0)
  //   return { totalQuantity, totalValue }
  // }, [detailData])

  // Effects
  useEffect(() => {
    fetchBranches()
  }, [fetchBranches])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  const formatTime = useCallback((dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return 'Invalid Time';
  }
}, []);

// Update this calculation based on your data structure
const detailTotals = useMemo(() => {
  const totalQuantity = detailData.reduce((sum, item) => sum + parseNumber(item.quantity), 0);
  const totalSalesValue = detailData.reduce((sum, item) => 
    sum + parseNumber(item.calculated_sold_total || item.total_amount), 0);
  const totalCostValue = detailData.reduce((sum, item) => 
    sum + parseNumber(item.calculated_stock_tatal || (item.quantity * item.unit_price)), 0);
  const totalProfit = totalSalesValue - totalCostValue;
  const profitMargin = totalSalesValue > 0 ? (totalProfit / totalSalesValue) * 100 : 0;
  const avgSalePrice = detailData.length > 0 ? 
    detailData.reduce((sum, item) => sum + parseNumber(item.price), 0) / detailData.length : 0;
  const avgCostPrice = detailData.length > 0 ? 
    detailData.reduce((sum, item) => sum + parseNumber(item.unit_price || item.cost_price), 0) / detailData.length : 0;

  return {
    totalQuantity,
    totalSalesValue,
    totalCostValue,
    totalProfit,
    profitMargin,
    avgSalePrice,
    avgCostPrice,
    totalValue: totalSalesValue // For backward compatibility
  };
}, [detailData, parseNumber]);

  // Memoized header section to prevent unnecessary re-renders
  const headerSection = useMemo(() => (
    <FixedHeaderContainer hidden={!headerVisible}>
      <StyledCard sx={{ mb: 3 }}>
        <HeaderCard>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                color="#1e293b"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <InventoryIcon />
                Inventory Management
              </Typography>
              <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                {selectedBranch 
                  ? "Select date range and apply filter to view inventory" 
                  : "Select a branch to view inventory"}
              </Typography>
            </Box>
            {selectedBranch && dateFilterApplied && (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchInventory(selectedBranch, startDate, endDate)}
                  disabled={loading}
                  sx={{ color: "#64748b", borderColor: "#d1d5db" }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitSelected}
                  disabled={selected.length === 0}
                  sx={{ ml: 2 }}
                >
                  Submit Selected ({selected.length})
                </Button>
              </Box>
            )}
          </Box>
        </HeaderCard>
        
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Select Branch</InputLabel>
            <Select
              value={selectedBranch || ""}
              onChange={(e) => handleBranchChange(e.target.value as string)}
              label="Select Branch"
              disabled={branchesLoading}
            >
              <MenuItem value="">
                <em>Select a branch</em>
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {branchesLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Loading branches...
              </Typography>
            </Box>
          )}
        </CardContent>
      </StyledCard>

      {selectedBranch && !loading && (
        <>
          {dateFilterApplied && inventory.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatsCard>
                  <Inventory2 sx={{ fontSize: 32, mb: 1, color: "#3b82f6" }} />
                  <Typography variant="h4" fontWeight="600" color="#1e293b">
                    {inventory?.length.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Total Items
                  </Typography>
                </StatsCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatsCard>
                  <AttachMoney sx={{ fontSize: 32, mb: 1, color: "#10b981" }} />
                  <Typography variant="h4" fontWeight="600" color="#1e293b" sx={{ fontSize: "1.5rem" }}>
                    {formatCurrency(statistics.totalValue)}
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Total Value
                  </Typography>
                </StatsCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatsCard>
                  <TrendingDown sx={{ fontSize: 32, mb: 1, color: "#f59e0b" }} />
                  <Typography variant="h4" fontWeight="600" color="#1e293b">
                    {statistics.lowStockCount}
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Low Stock
                  </Typography>
                </StatsCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatsCard>
                  <TrendingUp sx={{ fontSize: 32, mb: 1, color: "#ef4444" }} />
                  <Typography variant="h4" fontWeight="600" color="#1e293b">
                    {statistics.outOfStockCount}
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Out of Stock
                  </Typography>
                </StatsCard>
              </Grid>
            </Grid>
          )}

          <StyledCard sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Search Inventory"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setPage(0)
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                    <Button
                      variant="contained"
                      startIcon={<FilterList />}
                      onClick={handleDateFilter}
                      disabled={loading || !startDate || !endDate}
                      sx={{ minWidth: 'auto' }}
                    >
                      Apply Filter
                    </Button>
                    {(startDate || endDate) && (
                      <Button
                        variant="outlined"
                        startIcon={<Clear />}
                        onClick={clearDateFilter}
                        disabled={loading}
                        sx={{ minWidth: 'auto' }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
        </>
      )}
    </FixedHeaderContainer>
  ), [
    headerVisible, selectedBranch, branchesLoading, branches, loading, dateFilterApplied, 
    inventory.length, statistics, searchTerm, startDate, endDate, 
    handleBranchChange, fetchInventory, handleSubmitSelected, handleDateFilter, 
    clearDateFilter, formatCurrency, selected.length
  ])



  // Memoized table body rows to prevent unnecessary re-renders
  const tableRows = useMemo(() => {
    return paginatedInventory.map((item) => {
      const openingBalance = item.OpeningBalance || 0
      const openingValue = Number(item.OpeningValueBalance || '0')
      const purchasedQty = Number(item.TotalPurchasedBetween || '0')
      const soldQty = Number(item.TotalSoldBetween || '0')
      const itemPrice = Number(item.price || '0')
      const soldValue = Number(item.TotalAmountSoldBetween || '0')
      const closingBalance = item.ClosingBalance || 0
      const closingValue = Number(item.ClosingBalance * itemPrice || '0')
      const currentStock = item.CurrentStock || 0
      const currentStockValue = Number(item.CurrentStockValue || '0')
      const TotalOutFlowBetweenAmount = Number(item.TotalOutFlowBetween * itemPrice || '0')
      const purchasedValue = purchasedQty * itemPrice
      const stockStatus = getStockStatus(closingBalance)
      const isItemSelected = isSelected(item.product_id)
      const labelId = `inventory-table-checkbox-${item.product_id}`

      return (
        <TableRow 
          key={item.product_id} 
          hover
          onClick={(event) => handleClick(event, item.product_id)}
          role="checkbox"
          aria-checked={isItemSelected}
          tabIndex={-1}
          selected={isItemSelected}
        >
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              checked={isItemSelected}
              inputProps={{
                'aria-labelledby': labelId,
              }}
            />
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant="body1" fontWeight="500" color="#1e293b">
                {item.name || item.productName}
              </Typography>
              <Typography variant="caption" color="#64748b">
                ID: {item.product_id}
              </Typography>
              <br />
              <Typography variant="caption" color="#64748b">
                {item.last_updated ? formatDate(item.last_updated) : formatDate(item.dateCreated)}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Chip
              label={item.category || item.productCategory || item.type}
              size="small"
              sx={{ bgcolor: "#f1f5f9", color: "#475569" }}
            />
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant="body2" fontWeight="600" color="#1e293b">
                {formatCurrency(itemPrice)}
              </Typography>
              <Typography variant="caption" color="#64748b">
                Per unit
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="500" color="#1e293b">
              {openingBalance.toLocaleString()}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="600" color="#10b981">
              {formatCurrency(openingValue)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="600" color="#ef4444">
              -{(item.TotalOutFlowBetween || '0')}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="600" color="#ef4444">
              -{(TotalOutFlowBetweenAmount || '0')}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="500" color="#10b981">
              +{purchasedQty.toLocaleString()}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="600" color="#10b981">
              {formatCurrency(purchasedValue)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="500" color="#ef4444">
              -{soldQty.toLocaleString()}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="600" color="#ef4444">
              {formatCurrency(soldValue)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="600" color="#1e293b">
              {closingBalance.toLocaleString()}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="600" color="#10b981">
              {formatCurrency(closingValue)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="600" color="#1e293b">
              {currentStock.toLocaleString()}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="600" color="#10b981">
              {formatCurrency(currentStockValue)}
            </Typography>
          </TableCell>
          <TableCell>
            <Chip 
              label={stockStatus.label} 
              size="small" 
              color={stockStatus.color as any} 
            />
          </TableCell>
          <TableCell align="center">
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => { e.stopPropagation(); handleOpen(item); }}
              startIcon={<VisibilityIcon />}
              sx={{ color: "#64748b", borderColor: "#d1d5db" }}
            >
              View
            </Button>
          </TableCell>
        </TableRow>
      )
    })
  }, [paginatedInventory, isSelected, handleClick, handleOpen, formatDate, formatCurrency, getStockStatus])

  return (
    <IonPage>
      <IonContent>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {headerSection}

          {/* Show loading when branch is selected but data is loading */}
          {selectedBranch && loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading inventory data...
                </Typography>
              </Box>
            </Box>
          )}

          {/* Main Content Area */}
          {selectedBranch && !loading && (
            <StyledCard>
              <CardContent sx={{ p: 3, pt: 2 }}>
                {/* Show filter prompt when no dates selected or filter not applied */}
                {(!areBothDatesSelected || !dateFilterApplied) && (
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    py: 12,
                    bgcolor: "#f8fafc",
                    borderRadius: 2,
                    border: "1px dashed #e2e8f0",
                    textAlign: "center"
                  }}>
                    <FilterList sx={{ fontSize: 80, color: "#cbd5e1", mb: 3 }} />
                    <Typography variant="h4" color="#64748b" sx={{ mb: 2, fontWeight: 500 }}>
                      Select Date Range to View Inventory
                    </Typography>
                    <Typography variant="body1" color="#94a3b8" sx={{ mb: 4, maxWidth: 500 }}>
                      Please select both start and end dates, then click "Apply Filter" to load your inventory data
                    </Typography>
                    
                    {areBothDatesSelected && !dateFilterApplied && (
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<FilterList />}
                        onClick={handleDateFilter}
                        disabled={!startDate || !endDate}
                        sx={{ px: 4, py: 1.5 }}
                      >
                        Apply Filter to Load Data
                      </Button>
                    )}
                    
                    {!areBothDatesSelected && (
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                          label="Start Date"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ minWidth: 200 }}
                        />
                        <TextField
                          label="End Date"
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ minWidth: 200 }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {/* Show table only when filter is applied and data is available */}
                {dateFilterApplied && (
                  <>
                    <TableContainer 
                      ref={tableContainerRef}
                      component={Paper} 
                      onScroll={handleScroll}
                      sx={{ 
                        borderRadius: 2, 
                        border: "1px solid #e2e8f0",
                        maxHeight: '87vh',
                        overflow: 'auto',
                        position: 'relative'
                      }}
                    >
                      <Table stickyHeader>
                        <StickyTableHeader>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                indeterminate={selected.length > 0 && selected.length < paginatedInventory.length}
                                checked={paginatedInventory.length > 0 && selected.length === paginatedInventory.length}
                                onChange={handleSelectAllClick}
                                inputProps={{
                                  'aria-label': 'select all desserts',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Product</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Price</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Opening Balance</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Opening Value</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Transfer Out</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Transfer Out Amount</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Purchased</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Purchased Value</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Sold Qty</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Sold Value</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Closing Balance</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Closing Value</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Current Stock</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Current Stock Value</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="center">
                              Actions
                            </TableCell>
                          </TableRow>
                        </StickyTableHeader>
                        <TableBody>
                          {tableRows}
                        </TableBody>

                        {/* Footer with Totals - UPDATED WITH VIEW BUTTONS */}
                        {paginatedInventory.length > 0 && (
                          <TableFooter>
                            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                              <TableCell colSpan={4} align="right">
                                <Typography variant="subtitle1" fontWeight="600">
                                  Filtered Totals ({filteredInventory.length} items):
                                </Typography>
                              </TableCell>
                              
                              {/* Opening Balance */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600">
                                    {filteredTotals.totalOpeningBalance.toLocaleString()}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('opening-balance', 'Opening Balance Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Opening Value */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                    {formatCurrency(filteredTotals.totalOpeningValue)}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('opening-value', 'Opening Value Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Transfer Out Qty */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#ef4444">
                                    -{filteredTotals.totalOutFlowTotal.toLocaleString()}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('outflow-qty', 'Transfer Out Quantity Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Transfer Out Value */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#ef4444">
                                    {formatCurrency((filteredTotals.totalOutFlowTotalSum * -1))}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('outflow-value', 'Transfer Out Value Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Purchased Qty */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                    +{filteredTotals.totalPurchasedQty.toLocaleString()}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('purchased-qty', 'Purchased Quantity Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Purchased Value */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                    {formatCurrency(filteredTotals.totalPurchasedValue)}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('purchased-value', 'Purchased Value Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Sold Qty */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#ef4444">
                                    -{filteredTotals.totalSoldQty.toLocaleString()}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('sold-qty', 'Sold Quantity Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Sold Value */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#ef4444">
                                    {formatCurrency(filteredTotals.totalSoldValue)}
                                  </Typography>
                                  <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('sold-value', 'Sold Value Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              
                              {/* Closing Balance */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#1e293b">
                                    {filteredTotals.totalClosingBalance.toLocaleString()}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('closing-balance', 'Closing Balance Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Closing Value */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                    {formatCurrency(filteredTotals.closingValueTotal)}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('closing-value', 'Closing Value Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Current Stock */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#1e293b">
                                    {filteredTotals.totalCurrentStock.toLocaleString()}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('current-stock', 'Current Stock Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              {/* Current Stock Value */}
                              <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                  <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                    {formatCurrency(filteredTotals.totalCurrentStockValue)}
                                  </Typography>
                                  {/* <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewDetails('current-stock-value', 'Current Stock Value Details')}
                                      sx={{ p: 0.5, ml: 1 }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>
                              
                              <TableCell colSpan={2}></TableCell>
                            </TableRow>
                          </TableFooter>
                        )}
                      </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {filteredInventory.length > 0 && (
                      <TablePagination
                        component="div"
                        count={filteredInventory.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(Number.parseInt(e.target.value, 10))
                          setPage(0)
                        }}
                        sx={{ borderTop: "1px solid #e2e8f0", mt: 2 }}
                      />
                    )}
                  </>
                )}
              </CardContent>
            </StyledCard>
          )}

          {/* Show empty state when no branch is selected */}
          {!selectedBranch && !branchesLoading && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '400px',
              bgcolor: 'background.default'
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <InventoryIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  Select a branch to view inventory
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please choose a branch from the dropdown above to see inventory data
                </Typography>
              </Box>
            </Box>
          )}

          {/* Scroll to top button */}
          <Zoom in={showScrollTop}>
            <Fab
              size="medium"
              aria-label="scroll back to top"
              onClick={scrollToTop}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                bgcolor: '#1e293b',
                color: 'white',
                '&:hover': {
                  bgcolor: '#334155',
                },
              }}
            >
              <KeyboardArrowUp />
            </Fab>
          </Zoom>

          {/* Product Details Dialog */}
          <StyledDialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" fontWeight="600">
                  Product Details
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: "#64748b" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ py: 3 }}>
              {selectedInventory && (
                <Grid container spacing={3}>
                  {/* Basic Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="600" color="#1e293b" sx={{ mb: 2 }}>
                      Basic Information
                    </Typography>
                    <Box sx={{ bgcolor: "#f8fafc", p: 2, borderRadius: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="#64748b">
                            Product Name
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {selectedInventory.productName || selectedInventory.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="#64748b">
                            Product ID
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {selectedInventory.product_id}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="#64748b">
                            Category
                          </Typography>
                          <Chip
                            label={selectedInventory.productCategory || selectedInventory.category || selectedInventory.type}
                            size="small"
                            sx={{ bgcolor: "#e2e8f0", color: "#475569" }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="#64748b">
                            Last Updated
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {selectedInventory.last_updated ? formatDate(selectedInventory.last_updated) : formatDate(selectedInventory.dateCreated)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="#64748b">
                            Description
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {selectedInventory.description || "No description available"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Pricing Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="600" color="#1e293b" sx={{ mb: 2 }}>
                      Pricing Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#dcfce7", border: "1px solid #bbf7d0" }}>
                          <Typography variant="body2" color="#166534">
                            Purchase Price
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#166534">
                            {formatCurrency(selectedInventory.purchasePrice || selectedInventory.unit_price)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#dbeafe", border: "1px solid #bfdbfe" }}>
                          <Typography variant="body2" color="#1e40af">
                            Selling Price
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#1e40af">
                            {formatCurrency(selectedInventory.selling_price || selectedInventory.price || selectedInventory.minSalePrice)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Stock Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="600" color="#1e293b" sx={{ mb: 2 }}>
                      Stock Information {(startDate || endDate) && `(${startDate ? `from ${formatDate(startDate)}` : ''}${startDate && endDate ? ' to ' : ''}${endDate ? formatDate(endDate) : ''})`}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f0f9ff", border: "1px solid #e0f2fe" }}>
                          <Typography variant="body2" color="#0369a1">
                            Opening Balance
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#0369a1">
                            {(selectedInventory.OpeningBalance || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="#0369a1">
                            Value: {formatCurrency(selectedInventory.OpeningValueBalance || 0)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#dcfce7", border: "1px solid #bbf7d0" }}>
                          <Typography variant="body2" color="#166534">
                            Purchased
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#166534">
                            +{parseNumber(selectedInventory.TotalPurchasedBetween || '0').toLocaleString()}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#fef3c7", border: "1px solid #fde68a" }}>
                          <Typography variant="body2" color="#92400e">
                            Sold
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#92400e">
                            -{parseNumber(selectedInventory.TotalSoldBetween || '0').toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="#92400e">
                            Value: {formatCurrency(selectedInventory.TotalAmountSoldBetween || 0)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            bgcolor:
                              getStockStatus(selectedInventory.ClosingBalance || 0).color === "error"
                                ? "#fee2e2"
                                : getStockStatus(selectedInventory.ClosingBalance || 0).color === "warning"
                                  ? "#fef3c7"
                                  : "#dcfce7",
                            border: `1px solid ${
                              getStockStatus(selectedInventory.ClosingBalance || 0).color === "error"
                                ? "#fecaca"
                                : getStockStatus(selectedInventory.ClosingBalance || 0).color === "warning"
                                  ? "#fde68a"
                                  : "#bbf7d0"
                            }`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color={
                              getStockStatus(selectedInventory.ClosingBalance || 0).color === "error"
                                ? "#991b1b"
                                : getStockStatus(selectedInventory.ClosingBalance || 0).color === "warning"
                                  ? "#92400e"
                                  : "#166534"
                            }
                          >
                            Closing Balance
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="600"
                            color={
                              getStockStatus(selectedInventory.ClosingBalance || 0).color === "error"
                                ? "#991b1b"
                                : getStockStatus(selectedInventory.ClosingBalance || 0).color === "warning"
                                  ? "#92400e"
                                  : "#166534"
                            }
                          >
                            {(selectedInventory.ClosingBalance || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Value: {formatCurrency(selectedInventory.ClosingValueBalance || 0)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#e0f2fe", border: "1px solid #bae6fd" }}>
                          <Typography variant="body2" color="#0369a1">
                            Current Stock
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#0369a1">
                            {(selectedInventory.CurrentStock || 0).toLocaleString()}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#dcfce7", border: "1px solid #bbf7d0" }}>
                          <Typography variant="body2" color="#166534">
                            Current Stock Value
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#166534">
                            {formatCurrency(selectedInventory.CurrentStockValue || 0)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={handleClose}
                variant="contained"
                sx={{
                  bgcolor: "#1e293b",
                  "&:hover": { bgcolor: "#334155" },
                  textTransform: "none",
                  px: 3,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </StyledDialog>

          {/* Total Details Dialog */}
   <DetailDialog 
  open={detailDialogOpen} 
  onClose={handleCloseDetailDialog} 
  fullWidth 
  maxWidth="lg"
>
  <DialogTitle sx={{ pb: 1 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="h6" fontWeight="600">
        {metricTitle} ({startDate && endDate ? `${formatDate(startDate)} to ${formatDate(endDate)}` : ''})
      </Typography>
      <IconButton onClick={handleCloseDetailDialog} sx={{ color: "#64748b" }}>
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>
  <Divider />
  <DialogContent sx={{ py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
    {detailError && (
      <Alert severity="error" sx={{ mb: 2 }}>
     cc   {detailError}
      </Alert>
    )}
    
    {detailLoading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    ) : (
      <>
        {/* Enhanced Summary Stats for Your Data */}
        {detailData.length > 0 && (
          <Box sx={{ 
            bgcolor: '#f8fafc', 
            p: 2, 
            borderRadius: 2,
            border: '1px solid #e2e8f0'
          }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="#64748b">
                  Total Transactions
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  {detailData.length.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="#64748b">
                  Total Quantity Sold
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  {detailTotals.totalQuantity.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="#64748b">
                  Total Sales Value
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#10b981">
                  {formatCurrency(detailTotals.totalSalesValue)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="#64748b">
                  Total Cost Value
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#ef4444">
                  {formatCurrency(detailTotals.totalCostValue)}
                </Typography>
              </Grid>
              
              {/* Profit/Loss Summary */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="#64748b">
                  Total Profit/Loss
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight="600" 
                  color={detailTotals.totalProfit >= 0 ? '#10b981' : '#ef4444'}
                >
                  {formatCurrency(detailTotals.totalProfit)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="#64748b">
                  Profit Margin
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight="600" 
                  color={detailTotals.profitMargin >= 0 ? '#10b981' : '#ef4444'}
                >
                  {detailTotals.profitMargin.toFixed(2)}%
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="#64748b">
                  Avg. Sale Price
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  {formatCurrency(detailTotals.avgSalePrice)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="#64748b">
                  Avg. Cost Price
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  {formatCurrency(detailTotals.avgCostPrice)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Enhanced Detail Table for Your Data Structure */}
        {detailData.length > 0 ? (
          <TableContainer component={Paper} sx={{ mt: 2, maxHeight: '50vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Date/Time</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="right">Sale Price</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="right">Cost Price</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="right">Sales Total</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="right">Cost Total</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="right">Profit/Loss</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailData.map((item, index) => {
                  const salesTotal = parseNumber(item.calculated_sold_total || item.total_amount);
                  const costTotal = parseNumber(item.calculated_stock_tatal || (item.quantity * item.unit_price));
                  const profitLoss = salesTotal - costTotal;
                  
                  return (
                    <TableRow key={index} hover>
                      {/* Date & Time */}
                      <TableCell>
                        <Box>
                          <Typography variant="caption" color="#64748b" display="block">
                            {item.transaction_date || formatDate(item.created_at)}
                          </Typography>
                          <Typography variant="caption" color="#94a3b8">
                            {item.transaction_time || formatTime(item.created_at)}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      {/* Order ID */}
                      <TableCell>
                        <Typography variant="body2" fontWeight="500" sx={{ fontFamily: 'monospace' }}>
                          {item.order_id ? item.order_id.substring(0, 12) + '...' : 'N/A'}
                        </Typography>
                      </TableCell>
                      
                      {/* Product */}
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {item.product_name || 'Unnamed Product'}
                          </Typography>
                          <Typography variant="caption" color="#64748b">
                            ID: {item.product_id ? item.product_id.substring(0, 10) + '...' : 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      {/* Category */}
                      <TableCell>
                        <Chip
                          label={item.category || 'Uncategorized'}
                          size="small"
                          sx={{ bgcolor: '#f1f5f9', color: '#475569' }}
                        />
                      </TableCell>
                      
                      {/* Quantity */}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600">
                          {item.quantity?.toLocaleString() || '0'}
                        </Typography>
                      </TableCell>
                      
                      {/* Sale Price */}
                      <TableCell align="right">
                        <Typography variant="body2" color="#1e293b">
                          {formatCurrency(parseNumber(item.price))}
                        </Typography>
                      </TableCell>
                      
                      {/* Cost Price */}
                      <TableCell align="right">
                        <Typography variant="body2" color="#ef4444">
                          {formatCurrency(parseNumber(item.unit_price || item.cost_price))}
                        </Typography>
                      </TableCell>
                      
                      {/* Sales Total */}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600" color="#10b981">
                          {formatCurrency(salesTotal)}
                        </Typography>
                      </TableCell>
                      
                      {/* Cost Total */}
                      <TableCell align="right">
                        <Typography variant="body2" color="#ef4444">
                          {formatCurrency(costTotal)}
                        </Typography>
                      </TableCell>
                      
                      {/* Profit/Loss */}
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          {profitLoss >= 0 ? (
                            <TrendingUp sx={{ fontSize: 16, color: '#10b981' }} />
                          ) : (
                            <TrendingDown sx={{ fontSize: 16, color: '#ef4444' }} />
                          )}
                          <Typography 
                            variant="body2" 
                            fontWeight="600"
                            color={profitLoss >= 0 ? '#10b981' : '#ef4444'}
                          >
                            {formatCurrency(profitLoss)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color={profitLoss >= 0 ? '#10b981' : '#ef4444'}>
                          {salesTotal > 0 ? ((profitLoss / salesTotal) * 100).toFixed(2) : 0}%
                        </Typography>
                      </TableCell>
                      
                      {/* Status */}
                      <TableCell>
                        <Chip 
                          label={item.status || 'N/A'} 
                          size="small" 
                          color={
                            item.status === 'COMPLETED' || item.status === 'SERVED' ? 'success' :
                            item.status === 'PENDING' || item.status === 'WAITING' ? 'warning' :
                            item.status === 'REVERSED' ? 'error' : 'default'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: 600 }}>
                    Totals:
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {detailTotals.totalQuantity.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    -
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    -
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#10b981' }}>
                    {formatCurrency(detailTotals.totalSalesValue)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#ef4444' }}>
                    {formatCurrency(detailTotals.totalCostValue)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: detailTotals.totalProfit >= 0 ? '#10b981' : '#ef4444' }}>
                    {formatCurrency(detailTotals.totalProfit)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    -
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        ) : (
          !detailError && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 6,
              bgcolor: '#f8fafc',
              borderRadius: 2
            }}>
              <InventoryIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="body1" color="#64748b">
                No detailed data available
              </Typography>
            </Box>
          )
        )}
      </>
    )}
  </DialogContent>
  <Divider />
  <DialogActions sx={{ p: 3 }}>
    <Button
      onClick={handleCloseDetailDialog}
      variant="contained"
      sx={{
        bgcolor: "#1e293b",
        "&:hover": { bgcolor: "#334155" },
        textTransform: "none",
        px: 3,
      }}
    >
      Close
    </Button>
  </DialogActions>
</DetailDialog>
        </Container>
      </IonContent>
    </IonPage>
  )
}

export default Inventory_business